'use client'

import type { ReactNode } from 'react'

import { tw } from '@/lib/utils'

import { fillLabel } from './fill-label'
import { RatingDistribution, type RatingDistributionLabels } from './rating-distribution'
import { StarRatingDisplay } from './star-rating'

export interface RatingSummaryLabels extends RatingDistributionLabels {
  /** "{{score}} out of 5 stars" */
  starsOutOf5: string
  noReviewsYet: string
}

interface RatingSummaryProps {
  averageScore: number | null
  totalCount: number
  /** Review counts by whole star; absent on story bakes written before the
   *  breakdown existed, in which case only the average and count show. */
  distribution?: number[]
  /** A page for another model is loading: the last numbers stay up, dimmed. */
  loading: boolean
  /** The model filter, for families with more than one model. It lives in
   *  the card because it changes these numbers as much as the list. */
  filter?: ReactNode
  labels: RatingSummaryLabels
  className?: string
}

/** The evidence card: average, stars, how many reviews stand behind them, and
 *  the star breakdown. Sized as a fact, not a badge — one review should not
 *  look like a verdict. */
export const RatingSummary = ({
  averageScore,
  totalCount,
  distribution,
  loading,
  filter,
  labels,
  className,
}: RatingSummaryProps) => {
  const hasReviews = totalCount > 0 && averageScore != null
  const scoreText = (averageScore ?? 0).toFixed(1)

  return (
    <div
      className={tw('border-navy-20 rounded-[16px] border bg-white p-6', className)}
      aria-busy={loading || undefined}
    >
      {filter && <div className="border-navy-10 mb-5 border-b pb-5">{filter}</div>}
      <div className={tw('transition-opacity', loading && 'opacity-50')}>
        {hasReviews ? (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-navy text-[40px] leading-none font-semibold -tracking-[1px] tabular-nums">
                {scoreText}
              </span>
              <StarRatingDisplay
                score={averageScore}
                size={24}
                label={fillLabel(labels.starsOutOf5, { score: scoreText })}
              />
            </div>
            <p className="text-navy-80 mt-2 text-[16px]">
              {totalCount === 1 ? labels.reviewCountOne : fillLabel(labels.reviewCountOther, { count: totalCount })}
            </p>
            {distribution && (
              <RatingDistribution
                className="mt-5"
                distribution={distribution}
                totalCount={totalCount}
                labels={labels}
              />
            )}
          </>
        ) : (
          <p className="text-navy text-[20px] font-medium">{labels.noReviewsYet}</p>
        )}
      </div>
    </div>
  )
}
